import { Observable, Subject } from 'rxjs';
import { map, skipWhile, takeUntil, tap } from 'rxjs/operators';
import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';

import { UserService } from '@core/services/user/user.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { ZerosixLinkAccountModalComponent } from '@shared/modals/zerosix/zerosix-link-account-modal/zerosix-link-account-modal.component';
import { ZerosixError } from '@core/models/zerosix.model';
import { ZEROSIX_ERRORS } from '@config/values/zerosix-errors.values';

@Component({
  selector: 'app-zerosix-create-account-modal',
  templateUrl: './zerosix-create-account-modal.component.html',
  styles: [
    `
      // Permettre à la liste des pays de déborder de la modal sur les petits écrans
      :host {
        @media (max-width: 767px) {
          // Sélectionner la div modale principale
          .fixed.left-0.right-0 {
            overflow: visible !important;
          }

          // Sélectionner le conteneur blanc de la modal
          .rounded-t-3xl.bg-white {
            overflow: visible !important;
          }
        }
      }
    `,
  ],
})
export class ZerosixCreateAccountModalComponent
  extends ModalScrollBlockBase
  implements OnInit, OnDestroy
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'zerosix-create-account-modal';

  @Input() legalCheckboxLabel?: any;

  isDesktop$ = this.settingsSelector.selectDeviceIsDesktopScreen;

  index$: Observable<number> = this.modalSelector
    .selectModal(ZerosixCreateAccountModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  errorMessage?: string;
  phoneErrorMessage?: string;
  isValidating = false;
  step = 'join-form';

  phone = signal<string>('');

  constructor(
    private authSelector: AuthStoreSelector,
    private modalSelector: ModalStoreSelector,
    private readonly userService: UserService,
    private readonly modalsService: ModalsService,
    private settingsSelector: SettingsStoreSelector,
    private readonly zerosixService: ZerosixService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.authSelector.selectUser
      .pipe(
        takeUntil(this._destroyerRef),
        skipWhile(user => !user),
        tap(user => this.setPhoneNumber(user?.phone)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.modalsService.close(ZerosixCreateAccountModalComponent.handle);
  }

  onSubmit(): void {
    if (!this.phone() || this.isValidating) {
      return;
    }
    this.isValidating = true;
    this.errorMessage = undefined;
    this.phoneErrorMessage = undefined;

    this.zerosixService
      .createZerosixUser$(this.phone())
      .pipe(
        tap(user => this.userService.setUser(user)),
        tap(() => this.goToConfirmationStep()),
      )
      .subscribe({
        error: error => this.handleErrorResponse(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  private handleErrorResponse(error: ZerosixError): void {
    this.isValidating = false;

    if (error.class === 'ZerosixException') {
      if (error.code === 'ZEROSIX_MEMBER_NOT_FOUND') {
        this.phoneErrorMessage = 'zerosix.link-account.errors.account-not-found';
      }
      this.errorMessage = ZEROSIX_ERRORS[error.code];
      return;
    }
    this.errorMessage = 'errors.generic-error';
  }

  openLinkWithLoyaltyAccountModal(): void {
    this.close();
    this.modalsService.open(ZerosixLinkAccountModalComponent.handle);
  }

  private goToConfirmationStep(): void {
    this.step = 'confirmation';
  }

  private setPhoneNumber(phoneNumber?: string): void {
    this.phone.set(phoneNumber ?? '');
  }
}
